import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createEmploymentT } from '../../../utils/employmentTranslations'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import { Box } from '../../atoms/Box'
import { Icon } from '../../atoms/Icon'
import { AnalysisTabButtonMenu } from '../../molecules/AnalysisTabButtonMenu'
import { FilterDisplay } from '../../molecules/FilterDisplay'
import type { FilterItem } from '../../molecules/FilterDisplay'
import { FilterSelect } from '../../molecules/FilterPanel/FilterFormComponents'
import {
  EmploymentContainer,
  ContentContainer,
  LoadingContainer,
  ErrorContainer,
  FilterSection,
  FilterBar,
  LocationFilters,
  FilterChip as StyledFilterChip,
  LocationBadge,
  Divider,
} from './EmploymentContent.styles'

const getEmploymentTabs = (t: (key: string) => string) => [
  { id: 'overview', label: t('tabs.overview'), icon: 'briefcase' },
  { id: 'sectors', label: t('tabs.sectors'), icon: 'pie-chart' },
  { id: 'growth', label: t('tabs.growth'), icon: 'trending-up' },
  { id: 'business', label: t('tabs.business'), icon: 'building-2' },
]

type LocationType = 'city' | 'district' | 'radius' | 'walk' | 'bicycle' | 'car'

const getLocationIcon = (type?: LocationType) => {
  switch (type) {
    case 'city':
      return 'building-2'
    case 'district':
      return 'map'
    case 'walk':
      return 'footprints'
    case 'bicycle':
      return 'bike'
    case 'car':
      return 'car'
    default:
      return 'map-pin'
  }
}

export interface EmploymentContentProps {
  data: any
  loading?: boolean
  error?: string | null
  activeTab?: string
  onTabChange?: (tab: string) => void
  locationType?: LocationType
  onLocationTypeChange?: (type: LocationType) => void
  radiusDistance?: number
  onRadiusDistanceChange?: (distance: number) => void
  walkTime?: number
  onWalkTimeChange?: (time: number) => void
  bicycleTime?: number
  onBicycleTimeChange?: (time: number) => void
  carTime?: number
  onCarTimeChange?: (time: number) => void
  renderTabContent?: () => React.ReactNode
  getLocationLabel?: () => string
  /** Actual city name to display in filter chip (e.g., "Carouge") */
  cityName?: string
  /** Actual district name to display in filter chip (e.g., "Genève") */
  districtName?: string
  /** When true, hides filter controls and shows only the current filter value */
  readOnly?: boolean
}

export const EmploymentContent: React.FC<EmploymentContentProps> = ({
  data,
  loading,
  error,
  activeTab,
  onTabChange,
  locationType,
  onLocationTypeChange,
  radiusDistance,
  onRadiusDistanceChange,
  walkTime,
  onWalkTimeChange,
  bicycleTime,
  onBicycleTimeChange,
  carTime,
  onCarTimeChange,
  renderTabContent,
  getLocationLabel,
  cityName,
  districtName,
  readOnly = false,
}) => {
  const { t: rawT, i18n } = useTranslation()
  const t = createEmploymentT(rawT, i18n)
  const employmentTabs = useMemo(() => getEmploymentTabs(t), [t])
  // Build filter items for read-only display
  const filterItems: FilterItem[] = useMemo(() => {
    if (!readOnly || !getLocationLabel) return []
    return [
      {
        label: t('filters.location'),
        values: {
          value: getLocationLabel(),
          icon: getLocationIcon(locationType) as any,
        },
      },
    ]
  }, [readOnly, locationType, getLocationLabel, t])

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <Box>{t('loading.employment')}</Box>
        </LoadingContainer>
      )
    }

    if (error) {
      return (
        <ErrorContainer>
          <Box>
            {t('loading.error')} {error}
          </Box>
        </ErrorContainer>
      )
    }

    if (!data) {
      return (
        <LoadingContainer>
          <Box>{t('loading.noData')}</Box>
        </LoadingContainer>
      )
    }

    return renderTabContent?.()
  }

  return (
    <EmploymentContainer>
      <AnalysisSectionHeader
        title={t('section.title')}
        subtitle={t('section.subtitle')}
      />

      {/* Location Selector */}
      <FilterSection>
        <FilterBar>
          <LocationFilters>
            {!readOnly ? (
              <>
                {/* Location Type Chips */}
                {cityName && (
                  <StyledFilterChip
                    $active={locationType === 'city'}
                    onClick={() => onLocationTypeChange?.('city')}
                  >
                    <Icon name="building-2" size="small" />
                    {cityName}
                  </StyledFilterChip>
                )}
                {districtName && (
                  <StyledFilterChip
                    $active={locationType === 'district'}
                    onClick={() => onLocationTypeChange?.('district')}
                  >
                    <Icon name="map" size="small" />
                    {districtName}
                  </StyledFilterChip>
                )}

                {/* Travel Mode Chips */}
                {(cityName || districtName) && <Divider />}
                <StyledFilterChip
                  $active={locationType === 'walk'}
                  onClick={() => onLocationTypeChange?.('walk')}
                >
                  <Icon name="footprints" size="small" />
                  {t('filters.byWalk')}
                </StyledFilterChip>
                <StyledFilterChip
                  $active={locationType === 'bicycle'}
                  onClick={() => onLocationTypeChange?.('bicycle')}
                >
                  <Icon name="bike" size="small" />
                  {t('filters.byBicycle')}
                </StyledFilterChip>
                <StyledFilterChip
                  $active={locationType === 'car'}
                  onClick={() => onLocationTypeChange?.('car')}
                >
                  <Icon name="car" size="small" />
                  {t('filters.byCar')}
                </StyledFilterChip>

                {/* Dynamic selectors based on active type */}
                {locationType === 'radius' && (
                  <>
                    <Divider />
                    <FilterSelect
                      value={radiusDistance?.toString() ?? ''}
                      onChange={(value) =>
                        onRadiusDistanceChange?.(Number(value))
                      }
                      options={[
                        { value: '500', label: '500m' },
                        { value: '1000', label: '1km' },
                        { value: '2000', label: '2km' },
                        { value: '5000', label: '5km' },
                      ]}
                    />
                  </>
                )}

                {locationType === 'walk' && (
                  <>
                    <Divider />
                    <FilterSelect
                      value={walkTime?.toString() ?? ''}
                      onChange={(value) => onWalkTimeChange?.(Number(value))}
                      options={[
                        { value: '5', label: '5min' },
                        { value: '10', label: '10min' },
                        { value: '15', label: '15min' },
                      ]}
                    />
                  </>
                )}

                {locationType === 'bicycle' && (
                  <>
                    <Divider />
                    <FilterSelect
                      value={bicycleTime?.toString() ?? ''}
                      onChange={(value) => onBicycleTimeChange?.(Number(value))}
                      options={[
                        { value: '5', label: '5min' },
                        { value: '15', label: '15min' },
                        { value: '30', label: '30min' },
                      ]}
                    />
                  </>
                )}

                {locationType === 'car' && (
                  <>
                    <Divider />
                    <FilterSelect
                      value={carTime?.toString() ?? ''}
                      onChange={(value) => onCarTimeChange?.(Number(value))}
                      options={[
                        { value: '10', label: '10min' },
                        { value: '20', label: '20min' },
                        { value: '30', label: '30min' },
                      ]}
                    />
                  </>
                )}

                {/* Current Location Badge */}
                <LocationBadge>
                  <Icon name="map-pin" size="small" />
                  {getLocationLabel?.()}
                </LocationBadge>
              </>
            ) : (
              /* Read-only mode: show active filter using FilterDisplay */
              <FilterDisplay filters={filterItems} showDividers={false} />
            )}
          </LocationFilters>
        </FilterBar>
      </FilterSection>

      <AnalysisTabButtonMenu
        tabs={employmentTabs}
        activeTab={activeTab ?? 'overview'}
        onTabChange={onTabChange ?? (() => {})}
      />

      <ContentContainer>{renderContent()}</ContentContainer>
    </EmploymentContainer>
  )
}
